import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { EVENT_STATUSES, IEvent, ITeam, TeamUser } from '../../utils/types';
import UserAvatar from '../common/UserAvatar/UserAvatar';
import { Button, Icon, RatingBadge, TextField, Title } from '../common';
import { useAppSelector } from '../../store/hooks';
import { COLOURS } from '../../utils/consts';
import { useTranslation } from '../../hooks/translations/TranslationProvider';
import { truncateToOneDecimal } from '../../utils/helpers';
import EventInvitationDialog from './EventRequests/EventInvitations/EventInvitationDialog';
import { useState } from 'react';

interface EventDetailsTeamsProps {
    eventDetails: IEvent;
    onJoinEvent: (teamId: string) => void;
    onChangeTeam: (userId: string) => void;
    showJoinEventButton: boolean;
    isOwner: boolean;
    refreshEventData: () => void;
    foundUserInTeam?: TeamUser;
}

const EventTeams = ({
    eventDetails,
    onJoinEvent,
    onChangeTeam,
    showJoinEventButton,
    isOwner,
    refreshEventData,
    foundUserInTeam,
}: EventDetailsTeamsProps) => {
    const navigate = useLocalizedNavigate();
    const auth = useAppSelector(state => state.auth);
    const { t } = useTranslation();
    const [showInvitationDialog, setShowInvitationDialog] = useState(false);

    const userClickHandler = (userId: string) => {
        navigate(`/users/${userId}`);
    };

    function isOtherTeamFull(teamName: string) {
        const team = eventDetails.teams.find(t => t.name !== teamName)!;
        return team.users.length >= eventDetails.maxUsers / 2;
    }

    function renderJoinedUsers(teamIndex: number) {
        return eventDetails.teams[teamIndex].users.length;
    }

    const teamSize = eventDetails.maxUsers / 2;

    function renderUserRating(userId: string) {
        const rating = eventDetails.userRatings.find(
            rating => rating.userId === userId
        );
        return rating ? truncateToOneDecimal(rating.avgRating) : undefined;
    }

    const renderEmptySlots = (team: ITeam) => {
        const otherTeam = eventDetails.teams.find(
            otherTeam => otherTeam.name !== team.name
        );

        const isEventerInCurrentTeam = !!team?.users.find(
            user => user.id === auth.authData?.id
        );

        const isEventerInOtherTeam = !!otherTeam?.users.find(
            user => user.id === auth.authData?.id
        );

        const maxCounter = eventDetails.maxUsers / 2 - team.users.length;

        const jsx = [];
        let joinAdded = false;
        let nextEmptySlotIndex = -1;

        for (let i = 0; i < maxCounter; i++) {
            if (
                !isEventerInCurrentTeam &&
                !isEventerInOtherTeam &&
                !joinAdded &&
                eventDetails.status !== EVENT_STATUSES.CANCELLED &&
                eventDetails.status !== EVENT_STATUSES.CANCELLED_BY_SYSTEM &&
                showJoinEventButton
            ) {
                joinAdded = true;
                nextEmptySlotIndex = i + 1;
                jsx.push(
                    <div
                        key={`${eventDetails.id}_${team.name}_${i}`}
                        className="event-team-empty-slot-action"
                    >
                        <Icon
                            name="bx-user-plus"
                            color={COLOURS.gray}
                            size="30px"
                        />
                        <TextField size="s" color="subtitle">
                            {t('common.emptySlot')}
                        </TextField>
                        <Button
                            size="xs"
                            text={t('common.join')}
                            onClick={() => onJoinEvent(team.name)}
                            variant="green"
                        ></Button>
                    </div>
                );
            } else {
                jsx.push(
                    <div
                        className="event-team-empty-slot"
                        key={`${eventDetails.id}_${team.name}_${i}`}
                    >
                        <Icon
                            name="bx-user-plus"
                            color={COLOURS.gray}
                            size="30px"
                        />
                        <TextField size="s" color="subtitle">
                            {t('common.emptySlot')}
                        </TextField>
                    </div>
                );

                nextEmptySlotIndex = i;
            }
        }
        return jsx;
    };

    return (
        <div className="event-page-teams">
            {eventDetails.teams.map((team, index) => (
                <div key={team.name} className="event-details-team">
                    <div className="event-page-team-header">
                        <Title
                            text={`${t('common.team')} ${
                                index === 0 ? 'A' : 'B'
                            }`}
                            size="l"
                        />
                        <div className="badge badge-square badge-small badge-green">
                            <TextField size="s" color="green" weight="bold">
                                {`${renderJoinedUsers(index)}/${teamSize}`}
                            </TextField>
                        </div>
                    </div>

                    <>
                        {team.users.map(user => (
                            <div
                                className="event-team-user-wrapper"
                                key={user.id}
                            >
                                <div key={user.id} className="event-team-user">
                                    <UserAvatar
                                        imageSrc={user.avatarUrl}
                                        size="m"
                                        onClick={() =>
                                            userClickHandler(user.id)
                                        }
                                        averageRating={user.averageRating}
                                    />
                                    <TextField size="s" weight="bold">
                                        {user.username}
                                    </TextField>
                                </div>
                                {isOwner && !isOtherTeamFull(team.name) && (
                                    <Icon
                                        name="bx-transfer"
                                        onClick={() => onChangeTeam(user.id)}
                                        color={COLOURS.GREEN}
                                        size="26px"
                                        cursor="pointer"
                                        title="Change team"
                                    />
                                )}

                                {(foundUserInTeam?.hasVoted ||
                                    eventDetails.status ===
                                        EVENT_STATUSES.CLOSED) && (
                                    <div className="closed-event-stats">
                                        <div className="closed-event-rating">
                                            <RatingBadge
                                                score={renderUserRating(
                                                    user.id
                                                )}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {renderEmptySlots(team)}
                    </>
                </div>
            ))}
            <EventInvitationDialog
                isOpen={showInvitationDialog}
                eventDetails={eventDetails}
                onClose={() => setShowInvitationDialog(false)}
                isOwner={isOwner}
                refreshEventData={refreshEventData}
            />
        </div>
    );
};

export default EventTeams;
